import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, MoreHorizontal, Search, SlidersHorizontal } from 'lucide-react';
import { toast } from 'react-toastify';
import { Card } from '../ui/card.tsx';
import { useGetChildren, useDeleteChild } from '../../api/student/studentQueries.js';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';
import { Alert, AlertDescription } from '../ui/alert.tsx';
import { Badge } from '../ui/badge.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { BaseTable } from '../ui/base-table';
import { motion } from 'framer-motion';
import { InlineFilter } from '../ui/inline-filter.tsx';

function StudentDashboard() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: students, isLoading, error } = useGetChildren();
  const { mutate: deleteStudent, isPending: isDeleting } = useDeleteChild();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [filters, setFilters] = useState({
    status: {
      active: false,
      inactive: false
    },
    class: {
      assigned: false,
      unassigned: false
    },
    performance: {
      excellent: false,
      good: false,
      needsImprovement: false
    }
  });

  const handleResetFilters = () => {
    setFilters({
      status: {
        active: false,
        inactive: false
      },
      class: {
        assigned: false,
        unassigned: false
      },
      performance: {
        excellent: false,
        good: false,
        needsImprovement: false
      }
    });
  };

  const filterGroups = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { id: 'active', label: 'Active', checked: filters.status.active },
        { id: 'inactive', label: 'Inactive', checked: filters.status.inactive }
      ]
    },
    {
      id: 'class',
      label: 'Class',
      options: [
        { id: 'assigned', label: 'Assigned', checked: filters.class.assigned },
        { id: 'unassigned', label: 'Unassigned', checked: filters.class.unassigned }
      ]
    },
    {
      id: 'performance',
      label: 'Performance',
      options: [
        { id: 'excellent', label: '90%+', checked: filters.performance.excellent },
        { id: 'good', label: '70-89%', checked: filters.performance.good },
        { id: 'needsImprovement', label: '<70%', checked: filters.performance.needsImprovement }
      ]
    }
  ];

  const handleFilterChange = (groupId, optionId, checked) => {
    setFilters(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [optionId]: checked
      }
    }));
  };

  const columns = [
    {
      key: 'name',
      label: 'NAME',
      render: (student) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={student.avatar} alt={student.name} />
            <AvatarFallback>{student.name[0]}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-gray-900">{student.name}</div>
            <div className="text-sm text-gray-500">ID: {student.id}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'class',
      label: 'CLASS',
      render: (student) => (
        <div className="text-sm text-gray-600 text-center">
          {student.className || 'Not Assigned'}
        </div>
      ),
    },
    {
      key: 'attendance',
      label: 'ATTENDANCE',
      render: (student) => (
        <div className="flex gap-1.5 justify-center">
          {student.attendance && student.attendance.length > 0 ? (
            student.attendance.slice(0, 5).map((status, index) => (
              <div
                key={index}
                className={`h-2 w-2 rounded-sm ${
                  status === 'present' ? 'bg-green-500' : 'bg-red-500'
                }`}
              />
            ))
          ) : (
            <div className="text-sm text-gray-400">No attendance data</div>
          )}
        </div>
      ),
    },
    {
      key: 'performance',
      label: 'PERFORMANCE',
      render: (student) => (
        <div className="flex justify-center">
          {student.performance !== undefined ? (
            <div
              className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium
              ${
                student.performance >= 90
                  ? 'bg-green-100 text-green-700'
                  : student.performance >= 70
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {student.performance}%
            </div>
          ) : (
            <div className="text-sm text-gray-400">N/A</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (student) => (
        <div className="flex justify-center">
          <Badge
            variant="outline"
            className={`${
              student.isActive
                ? 'bg-green-50 text-green-700 hover:bg-green-50'
                : 'bg-red-50 text-red-700 hover:bg-red-50'
            }`}
          >
            {student.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      ),
    },
  ];

  const filterStudents = (studentsData) => {
    if (!Array.isArray(studentsData)) return [];
    
    return studentsData
      .filter(student => {
        // Search filter
        const searchMatches = 
          `${student?.firstName} ${student?.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
          student?.childId?.toString().includes(searchQuery) ||
          student?.class?.toLowerCase().includes(searchQuery.toLowerCase());

        if (!searchMatches) return false;

        // Status filter
        if (filters.status.active || filters.status.inactive) {
          const isActive = student.status === 'active';
          if (filters.status.active && !isActive) return false;
          if (filters.status.inactive && isActive) return false;
        }

        // Class filter
        if (filters.class.assigned || filters.class.unassigned) {
          const hasClass = student.class && student.class !== 'Not Assigned';
          if (filters.class.assigned && !hasClass) return false;
          if (filters.class.unassigned && hasClass) return false;
        }

        // Performance filter
        if (filters.performance.excellent || filters.performance.good || filters.performance.needsImprovement) {
          const performance = student.performance || 0;
          if (filters.performance.excellent && performance < 90) return false;
          if (filters.performance.good && (performance < 70 || performance >= 90)) return false;
          if (filters.performance.needsImprovement && performance >= 70) return false;
        }

        return true;
      })
      .map(student => ({
        id: student.childId,
        name: `${student.firstName} ${student.lastName}`,
        className: student.class || 'Not Assigned',
        attendance: student.attendance || [],
        performance: student.performance || 0,
        teacher: student.teacher || 'Not Assigned',
        isActive: student.status === 'active',
        avatar: student.photoUrl || '/placeholder.svg'
      }));
  };

  const handleDelete = async (student, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await deleteStudent(student.id);
        toast.success('Student deleted successfully');
      } catch (error) {
        toast.error('Failed to delete student');
      }
    }
  };

  const rowActions = (student) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isDeleting}>
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={(e) => {
          e.stopPropagation();
          navigate(`/student/details/${student.id}`);
        }}>
          View details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(e) => {
          e.stopPropagation();
          navigate(`/student/edit/${student.id}`);
        }}>
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600"
          onClick={(e) => handleDelete(student, e)}
          disabled={isDeleting}
        >
          {isDeleting && selectedStudent?.id === student.id ? 'Deleting...' : 'Delete'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderContent = () => {
    if (error) {
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load students: {error.message}
          </AlertDescription>
        </Alert>
      );
    }

    const filteredStudents = filterStudents(students);
    const totalItems = filteredStudents.length;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-4 flex-1 max-w-[600px]"
            layout
          >
            <div className="flex-1 flex items-center gap-4 min-w-0">
              <InlineFilter 
                groups={filterGroups}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
              />
              <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, ID or class"
                  className="w-full pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </motion.div>

          <Button
            onClick={() => navigate('/student/add')}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Student
          </Button>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white">
          <BaseTable
            columns={columns}
            data={filteredStudents}
            rowActions={rowActions}
            onRowClick={(row) => navigate(`/student/details/${row.id}`)}
            pageSize={pageSize}
            currentPage={currentPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col">
      <main className="flex-1 overflow-auto p-6">
        {renderContent()}
      </main>
    </div>
  );
}

export default StudentDashboard;
